import React from 'react';

import { useIntl } from 'react-intl';

import {
	mediaInsertMessages,
	toolbarInsertBlockMessages as messages,
} from '@atlaskit/editor-common/messages';
import AiSparkleIcon from '@atlaskit/icon/core/ai-sparkle';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type { AIImageGenerationPlugin } from './aiImageGenerationPluginType';
import { showAIImageGenerationPopup } from './pm-plugins/actions';
import { createImageGenPlugin } from './pm-plugins/main';
import { pluginKey } from './pm-plugins/plugin-key';
import { AIImageGenerationPopup } from './ui/AIImageGenerationPopup';
import { MediaInsertImageGenerationTab } from './ui/MediaInsertImageGenerationTab';

const MEDIA_INSERT_AI_TAB_KEY = 'ai-image-generation';

/**
 * Renders the "Generate with AI" tab label using the same
 * `intl.formatMessage(...)` pattern that `MediaInsertPicker` uses for its
 * built-in `fileTabTitle` and `linkTabTitle` tabs. Defined at module scope so
 * the component reference is stable across renders.
 */
const GenerateTabLabel = (): React.JSX.Element => {
	const intl = useIntl();
	return <>{intl.formatMessage(mediaInsertMessages.generateTabTitle)}</>;
};

export const aiImageGenerationPlugin: AIImageGenerationPlugin = ({ api }) => {
	// Register a "Generate with AI" tab on the media insert picker. This keeps
	// the public `editor-plugin-media-insert` package free of `@atlassian/*`
	// dependencies — it only knows about the registry interface, not about
	// AI image generation.
	if (expValEquals('cc_page_experiences_editor_image_generation', 'isEnabled', true)) {
		api?.mediaInsert?.actions.registerInsertTab({
			key: MEDIA_INSERT_AI_TAB_KEY,
			label: <GenerateTabLabel />,
			component: MediaInsertImageGenerationTab,
		});
	}

	return {
		name: 'aiImageGeneration',

		pmPlugins() {
			return [
				{
					name: 'aiImageGeneration',
					plugin: () => createImageGenPlugin(),
				},
			];
		},

		getSharedState(editorState) {
			if (!editorState) {
				return { isOpen: false };
			}
			const state = pluginKey.getState(editorState);
			if (!state) {
				return { isOpen: false };
			}
			return { isOpen: state.isOpen };
		},

		commands: {
			showAIImageGenerationPopup:
				() =>
				({ tr }) =>
					showAIImageGenerationPopup(tr),
		},

		contentComponent: ({
			editorView,
			popupsMountPoint,
			popupsBoundariesElement,
			popupsScrollableElement,
		}) => {
			if (!editorView) {
				return null;
			}

			return (
				<AIImageGenerationPopup
					api={api}
					editorView={editorView}
					popupsMountPoint={popupsMountPoint}
					popupsBoundariesElement={popupsBoundariesElement}
					popupsScrollableElement={popupsScrollableElement}
				/>
			);
		},

		pluginsOptions: {
			quickInsert: ({ formatMessage }) => {
				if (!expValEquals('cc_page_experiences_editor_image_generation', 'isEnabled', true)) {
					return [];
				}

				const title = formatMessage(messages.aiImageGeneration);

				return [
					{
						id: 'aiImageGeneration',
						title,
						description: formatMessage(messages.aiImageGenerationDescription),
						priority: 500,
						keywords: ['ai', 'generate', 'image', 'create', 'picture'],
						icon: () => <AiSparkleIcon label={title} color="currentColor" />,
						isDisabledOffline: true,
						action(insert) {
							const tr = insert('');
							api?.aiImageGeneration.commands.showAIImageGenerationPopup()({ tr });
							return tr;
						},
					},
				];
			},
		},
	};
};
